<template>
	<div>
		<div class="row mb-8">
			<div class="col-md-3"><h1>{{ $t("absences") }}</h1></div>
			<div class="col-md-9">
                <div class="d-flex flex-row align-items-end flex-wrap float-right" style="gap: 1rem" >
                    <DateInput :label="$t('from')" v-model="datetime.from" @input="getAllAbsences()"/>
                    <DateInput :label="$t('to')" v-model="datetime.to" @input="getAllAbsences()" />
                    <button class="btn btn-primary mb-3" @click="openAbsenceModal" data-cy="new-absence-button">
                        {{ $t("new-absence") }}
                    </button>
                </div>
			</div>
		</div>
		<div class="inspector-list full-width">
			<div
				class="inspector-list-item pt-2 pb-2"
				:class="{ active: absence.editing }"
				@click="() => selectAbsence(absence)"
				v-for="(absence, index) in absences"
				data-cy="absence-list-item"
				:key="index"
			>
				<h2 class="mt-0 mb-0">{{ absence.student_displayname }}</h2>
				<p class="font-weight-bold mt-0 mb-0" v-if="absence.studentid">{{$t('id-colon')}} {{absence.studentid}}</p>
				<p class="mt-0 mb-0" v-if="absence.draft">{{ $t("draft") }}</p>
				<p class="mt-0 mb-1 clearfix" v-if="!absence.draft">
					<LabelIndicator :type="getAbsenceIndicatorType(absence.type)">
						{{ getAbsenceCaption(absence.type) }}
					</LabelIndicator>
				</p>
				<p class="mt-0 mb-0" v-if="!absence.draft" data-cy="absence-item-date">
					{{ getAbsenceDate(absence) }}
				</p>
			</div>

			<div
				class="inspector-list-detail"
				:class="{ active: selectedAbsence }"
				data-cy="absence-list-detail"
			>
				<a
					class="btn btn-light btn-xs float-right ml-3"
					v-on:click="closeView()"
					><b-icon icon="x" width="1em" height="1em"></b-icon>
				</a>

				<p v-if="!selectedAbsence">{{ $t("select-an-absence-to-view-slash-manage-it") }}</p>

				<div v-if="selectedAbsence">
					<h1 class="mb-4">{{ selectedAbsence.student_displayname }}</h1>

					<div class="mb-4">
						<h5>{{$t('type-of-absence')}}</h5>
						<div>
							<select
								class="form-control"
								id="absence-clinic"
								name="absence-clinic"
								data-cy="absence-type"
								v-model="selectedAbsence.type"
							>
								<option value="">{{ $t("please-select") }}</option>
								<option
									v-for="absenceType in absenceTypes"
									:key="absenceType.id"
									:value="absenceType.id"
									>{{ absenceType.title }}</option
								>
							</select>
						</div>
					</div>

					<div class="mb-4">
						<h5>{{$t('start')}}</h5>
						<div>
							<input
								class="form-control"
								type="datetime-local"
								id="absence-start"
								name="absence-start"
								v-model="selectedAbsence.start_datetime"
								:max="selectedAbsence.end_datetime"
							/>
						</div>
					</div>

					<div class="mb-4">
						<h5>{{$t('end')}}</h5>
						<div>
							<input
								class="form-control"
								type="datetime-local"
								id="absence-end"
								name="absence-end"
								data-cy="absence-end"
								v-model="selectedAbsence.end_datetime"
								:min="selectedAbsence.start_datetime"
							/>
						</div>
					</div>

					<div class="mb-4">
						<h5>{{ $t("specialty") }}</h5>
						<div>
							<select
								class="form-control"
								id="absence-specialty"
								name="absence-specialty"
								data-cy="absence-specialty"
								v-model="selectedAbsence.specialty"
							>
								<option value="">{{ $t("none") }}</option>
								<option
									:value="specialty.id"
									v-for="specialty in specialties"
									:key="specialty.id"
									>{{ specialty.name }}</option
								>
							</select>
						</div>
					</div>

					<div class="mb-4">
						<h5>{{ $t("clinic") }}</h5>
						<div>
							<select
								class="form-control"
								id="absence-clinic"
								name="absence-clinic"
								data-cy="absence-clinic"
								v-model="selectedAbsence.clinic"
							>
								<option value="">{{ $t("none") }}</option>
								<option
									v-for="clinic in clinics"
									:value="clinic.id"
									:key="clinic.id"
									>{{ clinic.name }}</option
								>
							</select>
						</div>
					</div>

					<div class="mb-4">
						<h5>{{ $t("comments") }}</h5>
						<div>
							<textarea
								class="form-control"
								id="absence-comment"
								name="absence-comment"
								data-cy="absence-comment"
								v-model="selectedAbsence.comment"
							></textarea>
						</div>
					</div>

					<div>
						<button
							class="btn btn-primary"
							@click="saveAbsence"
							:disabled="savingAbsence"
							data-cy="save-absence-button"
						>
							{{ $t("save") }}
						</button>
                        <button
                            class="btn btn-danger ml-2"
                            @click="openDeleteAbsenceModal(selectedAbsence.id)"
                            data-cy="delete-absence-button"
                            >
						    {{ $t("delete") }}
					    </button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import LabelIndicator from "src/components/LabelIndicator.vue";
import DateInput from "src/components/Inputs/DateInput";

export default {
	data: function() {
        const today = new Date();
        const future = new Date(today);
        future.setDate(today.getDate() + 7);

		return {
			selectedStudent: null,
			selectedAbsence: null,
			savingAbsence: false,
			startDate: null,
			endDate: null,
			absences: [],
            datetime: {
                from: today.toISOString().split('T')[0],
                to: future.toISOString().split('T')[0]
            },
            students:[]
		};
	},
	components: {
		LabelIndicator,
        DateInput
	},
	mounted() {
		this.getCurrentAbsences();
        this.getStudents();
	},
	methods: {
		unableToLoadAbsences() {
			const self = this;
			this.$store.commit("openDialog", {
				title: this.$t("oops"),
				content: this.$t("unable-to-get-absences"),
				confirmCallBack: function() {
					self.$router.replace("/attendance");
				}
			});
		},
		unableToSaveAbsence() {
			this.$store.commit("openDialog", {
				title: this.$t("oops"),
				content: this.$t("unable-to-save-absence")
			});
		},
        unableToDeleteAbsence() {
			this.$store.commit("openDialog", {
				title: this.$t("oops"),
				content: this.$t("unable-to-delete-absence")
			});
		},
		getCurrentAbsences() {
			this.$cfetch(`${this.$settings.APIBaseURL}/absences/listcurrent`, {
				cache: true,
				method: "get"
			})
				.then(json => {
					if (json.status === "SUCCESS") {
						for (var index in json.result) {
							json.result[index].editing = false;
							json.result[index].draft = false;
						}
						this.absences = json.result;
					} else {
						this.$log.error.call(this, "Could not fetch absences");
						this.unableToLoadAbsences();
					}
				})
				.catch(error => {
					this.$log.error.call(this, error);
					this.unableToLoadAbsences();
				});
		},
        getAllAbsences() {
			this.$cfetch(`${this.$settings.APIBaseURL}/absences/all`, {
				cache: true,
                data: {
                    from: this.datetime.from,
                    to: this.datetime.to
                },
				method: "POST"
			})
				.then(json => {
					if (json.status === "SUCCESS") {
						for (const result of json.results) {
							result.editing = false;
							result.draft = false;
                            result.start = result.dateabsent;
                            result.end = result.dateabsentend;
                            result.comment = result.absencecomment;
                            result.specialty = result.specialty_id;
                            result.clinic = result.clinic_id;

                            const studentMatch = this.students.find(item => item.id === result.student_id);
                            result.student_displayname = studentMatch ? studentMatch.displayName : '';
                            result.studentid = studentMatch ? studentMatch.studentid : '';
							result.student = studentMatch ? studentMatch.id : result.student_id;
						}
						this.absences = json.results;
					} else {
						this.$log.error.call(this, "Could not fetch absences");
						this.unableToLoadAbsences();
					}
				})
				.catch(error => {
					this.$log.error.call(this, error);
					this.unableToLoadAbsences();
				});
		},
        getStudents() {
            this.$cfetch(`${this.$settings.APIBaseURL}/getusers`,
                {
                    method: "post",
                    data: {
                        priv: 'APP_STUDENT'
                    }
                }
            )
            .then(res => {
                if (res.status == "SUCCESS") {
                	this.students = res.users;
                }
            })
            .catch(error => {
                this.$log.error.call(this, error);
            });
		},
        deleteAbsence(id){
            this.$cfetch(`${this.$settings.APIBaseURL}/absences/delete`, {
				method: "POST",
                data: {
                    id: id
                }
			})
            .then(json => {
					if (json.status === "SUCCESS") {
                        this.closeView();
                        this.getCurrentAbsences();
					} else {
						this.$log.error.call(this, "Could not delete absence");
						this.unableToDeleteAbsence();
					}
				})
				.catch(error => {
					this.$log.error.call(this, error);

				});
        },
		saveAbsence() {
			// a second tap while saving would insert the absence twice (it has no id until the response)
			if (this.savingAbsence) {
				return;
			}
			if (this.selectedAbsence) {
				if (this.selectedAbsence.start_datetime) {
					this.selectedAbsence.start = this.$datetime.getTimeStampFromDateTimeField(
						this.selectedAbsence.start_datetime,
						false
					);
				}
				if (this.selectedAbsence.end_datetime) {
					this.selectedAbsence.end = this.$datetime.getTimeStampFromDateTimeField(
						this.selectedAbsence.end_datetime,
						false
					);
				}
				this.savingAbsence = true;
				this.$cfetch(`${this.$settings.APIBaseURL}/absences/save`, {
					cache: false,
					method: "post",
					data: {
						absence: this.selectedAbsence
					}
				})
					.then(json => {
						if (json.status === "SUCCESS") {
							this.selectedAbsence.id = json.id;
							this.selectedAbsence.editing = false;
							this.selectedAbsence.draft = false;
							this.selectedAbsence = null;
						} else {
							this.$log.error.call(this, "Could not save absence");
							this.unableToSaveAbsence();
						}
					})
					.catch(error => {
						this.$log.error.call(this, error);
						this.unableToSaveAbsence();
					})
					.finally(() => {
						this.savingAbsence = false;
					});
			}
		},
		selectAbsence(absence) {
			if (this.selectedAbsence) {
				this.selectedAbsence.editing = false;
			}
			absence.editing = true;
			if (absence.start) {
				absence.start_datetime = this.$datetime.getDateTimeFieldValueFromTimestamp(
					absence.start ,
					false
				);
			}
			if (absence.end) {
				absence.end_datetime = this.$datetime.getDateTimeFieldValueFromTimestamp(
					absence.end ,
					false
				);
			}
			this.selectedAbsence = absence;
		},
		closeView() {
			this.selectedAbsence.editing = false;
			this.selectedAbsence = null;
		},
		openAbsenceModal() {
			const self = this;
			this.$users.getStudents.call(this).then(students => {
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t("select-student"),
					selectCallback: function(selection) {
						this.selectedStudent = selection;
						self.$store.commit("closeModal");

						//self.startNewAbsence(selection);
						self.checkAndStartNewAbsence(selection);
						

						// if(self.search.student == selection.id){
						// 	self.search.student_displayname = '';
						// 	self.search.student = selection.null;
						// } else {
						// 	self.search.student_displayname = selection.displayName;
						// 	self.search.student = selection.id;
						// }
						// self.$store.commit("closeModal");
					},
					selected: [
						{
							id: this.selectedStudentId
						}
					],
					data: students,
					captionKey: "displayName",
					studentSearchFilters: true
				});
			});
		},
        openDeleteAbsenceModal(id) {
            const self = this;
            this.$store.commit("openDialog", {
                cancelBtn: true,
                title: this.$t("delete-absence-question-mark"),
                content: this.$t('this-absence-will-be-permanently-deleted'),
                confirmCallBack: function () {
                    self.deleteAbsence(id);
                },
            });
		},
		
		checkAndStartNewAbsence(student) {
			const newAbsenceFrom = new Date(this.datetime.from).getTime() / 1000;
			const newAbsenceTo = new Date(this.datetime.to).getTime() / 1000;

			const overlappingAbsences = this.absences.filter(a => {
				if (!a.student || a.student !== student.id) return false;
				const absenceStart = a.start;
				let absenceEnd = a.end;
				if (!absenceEnd) {
					const absenceDate = this.$datetime.getDateFieldValueFromTimestamp(a.start);
					absenceEnd = new Date(absenceDate + "T23:59:59").getTime() / 1000;
				}
				return absenceStart < newAbsenceTo && absenceEnd > newAbsenceFrom;
			});

			if (overlappingAbsences.length > 0) {
				let message = `<p>Overlap for this datetime range: <strong>${this.datetime.from} → ${this.datetime.to}</strong></p><pre>`;
				overlappingAbsences.forEach(a => {
					const typeStr = this.getAbsenceCaption(a.type);
					const startStr = `${this.$datetime.timestampToDate(a.start)} ${this.$datetime.timestampToTime(a.start)}`;
					const endStr = a.end
						? `${this.$datetime.timestampToDate(a.end)} ${this.$datetime.timestampToTime(a.end)}`
						: this.$t("not-updated");

					message += `${typeStr}\nStart: ${startStr}\nEnd: ${endStr}\n\n`;
				});
				message += "</pre>";
				message += "<em>Are you sure you want to add another absence?</em>";

				this.$store.commit("openDialog", {
					cancelBtn: true,
					title: "Overlap Warning",
					htmlContent: message,
					confirmCallBack: () => {
						this.startNewAbsence(student);
					},
					cancelCallBack: () => {}
				});
			} else {
				this.startNewAbsence(student);
			}
		},


		startNewAbsence(student) {
			const currentTimestamp = Math.floor(Date.now() / 1000);
			const newAbsence = {
			id: null,
			editing: false,
			start: currentTimestamp, 
			start_datetime: new Date(currentTimestamp * 1000)
								.toISOString()
								.replace(/:[0-9]+.[0-9]+[A-Z]/, ""),
			end: null,
			end_datetime: null,
			type: null,
			student: student.id,
			student_displayname: student.displayName,
			studentid: student.studentid,
			teacher: null,
			teacher_displayname: "",
			specialty: null,
			clinic: null,
			comment: "",
			draft: true
			};

			this.absences.push(newAbsence);
			this.selectedAbsence = newAbsence;

			if (this.isTeacher) {
			this.selectedAbsence.teacher = this.$UserSession.getValue("id");
			this.selectedAbsence.teacher_displayname = this.$UserSession.getValue("displayName");
			}
		},
		getAbsenceCaption(id) {
			var title = this.$t("type-unspecified");
			this.absenceTypes.forEach(item => {
				if (item.id == id) {
					title = item.title;
				}
			});
			return title;
		},
		getAbsenceIndicatorType(type) {
			if (Number(type) === 1 || Number(type) === 2) {
				return "success";
			}

			if (Number(type) === 3 || Number(type) === 4 || Number(type) === 5) {
				return "danger";
			}

			return "";
		},
		getAbsenceDate(absence) {
			if (absence.end) {
				const isToday = this.$datetime.isToday(absence.end);
				const isTommorow = this.$datetime.isTommorow(absence.end);
				const isThisWeek = this.$datetime.isThisWeek(absence.end);
				const endTime = this.$datetime.timestampToTime(absence.end, false);
				const endDay = this.$datetime.timestampToDay(absence.end);
				const endDate = this.$datetime.timestampToDate(absence.end);

				if (isToday) {
					return this.$t("ends-today-at")+' '+endTime;
				}

				if (isTommorow) {
					return this.$t("ends-tomorrow-at")+' '+ endTime;
				}

				if (isThisWeek) {
					return this.$t("ends-on")+' '+ endDay +' ' + this.$t("date-on")+' ' + endTime;
				}

				return this.$t("ends-on")+' '+ endDate;
			} else {
				const startTime = this.$datetime.timestampToTime(absence.start, false);
				return this.$t("today-at")+' '+ startTime;
			}
		}
	},
	computed: {
		isTeacher: function() {
			return this.$UserSession.hasPriv.call(this, "MARK_TREATMENTSESSIONS");
		},
		specialties: function() {
			return this.$config.getValue("specialties");
		},
		clinics: function() {
			return this.$config.getValue("clinics");
		},
		absenceTypes: function() {
			const obj = this.$config.getValue("absencetypes");
			if (obj) {
				return Object.values(this.$config.getValue("absencetypes"));
			}
			return [];
		},
        filteredAbsences(){
            let filtered = this.absences;
            let from = this.datetime.from;
            let to = this.datetime.to;

            if (from) {
                filtered = filtered.filter(item =>
                    this.$datetime.getDateFieldValueFromTimestamp(item.start) >= from
                )
            }

            if (to) {
                filtered = filtered.filter(item =>
                    this.$datetime.getDateFieldValueFromTimestamp(item.end) <= to
                )
            }

            return filtered;
        }
	}
};
</script>
